import type { DropdownMenuItem } from '@nuxt/ui';
import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { usePageDisplayMode } from '#layers/blocks/composables/block-context/use-page-display-mode';
import { getRelatedItemDetailRoute } from '#layers/blocks/utils/relation/related-item-detail-route';
import { getCollectionPrimaryKeyFields, getM2MDefaultDisplayTemplate, getM2MStructure, isM2M, RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/spaces/public';

export type RelatedValuesDropdownItem =
	| { type: 'separator' }
	| { slot: 'view-more'; to?: DropdownMenuItem['to'] }
	| { to?: DropdownMenuItem['to']; value: AnyItem };

export interface UseRelatedValuesDropdownItemsOptions {
	enabled: MaybeRefOrGetter<boolean>;
	field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>;
	item: MaybeRefOrGetter<AnyItem>;
	relatedItems: MaybeRefOrGetter<AnyItem[]>;
}

/**
 * Lazily derives dropdown items only when the menu is actually opened.
 */
export function useRelatedValuesDropdownItems(options: UseRelatedValuesDropdownItemsOptions) {
	const enabled = computed(() => toValue(options.enabled));
	const field = computed(() => toValue(options.field));
	const item = computed(() => toValue(options.item));
	const relatedItems = computed(() => toValue(options.relatedItems));
	const pageDisplayMode = usePageDisplayMode();
	// Entries stay plain rows on a page shown in a drawer or modal, where a link would navigate the
	// page underneath instead.
	const navigable = computed(() => !pageDisplayMode.value);

	// Resolve the M2M structure lazily (it triggers rstore relation resolution) and only when the
	// dropdown is open, so collapsed cells in a list/table stay cheap.
	const m2mStructure = computed(() => {
		if (!enabled.value || !isM2M(field.value)) return null;

		try {
			return getM2MStructure(field.value);
		}
		catch {
			return null;
		}
	});

	// M2M rows render the junction row via a `{{ toTarget }}` template; O2M/M2O get `undefined`,
	// falling back to the opposite collection's own default (current behavior).
	const displayTemplate = computed(() => m2mStructure.value
		? getM2MDefaultDisplayTemplate(m2mStructure.value)
		: undefined);

	// Click target: the TARGET collection for M2M, else the opposite collection (equal for non-M2M).
	const routeCollection = computed(() => m2mStructure.value
		? m2mStructure.value.target.field.collection
		: field.value.oppositeCollection);

	const { detailRoute: thisCollectionDetailRoute } = useCollectionRoutes(() => field.value.collectionId);
	const { detailRoute: routeDetailRoute } = useCollectionRoutes(() => routeCollection.value);

	const routePrimaryKeyFields = computed(() => enabled.value && routeCollection.value
		? getCollectionPrimaryKeyFields(routeCollection.value)
		: []);
	const thisCollectionPrimaryKeyFields = computed(() => enabled.value
		? getCollectionPrimaryKeyFields(field.value.collection!)
		: []);

	const limitedItems = computed(() => enabled.value
		? relatedItems.value.slice(0, RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT)
		: []);
	const hasMore = computed(() => enabled.value && relatedItems.value.length > RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT);
	const viewMoreRoute = computed(() => enabled.value && navigable.value
		? getRelatedItemDetailRoute(item.value, thisCollectionPrimaryKeyFields.value, thisCollectionDetailRoute)
		: undefined);

	// The PK lives on the target nested under the junction's toTarget leg for M2M; for O2M/M2O the
	// row itself is the target. The dropdown still renders the (junction) row as-is.
	function routeKeySource(row: AnyItem): AnyItem | null {
		const structure = m2mStructure.value;

		if (!structure) return row;

		return (row[structure.junction.toTarget.apiName] as AnyItem | undefined) ?? null;
	}

	const dropdownItems = computed<RelatedValuesDropdownItem[]>(() => {
		if (!enabled.value) {
			return [];
		}

		const items = limitedItems.value.map((row) => {
			const keySource = routeKeySource(row);

			return {
				value: row,
				to: navigable.value && keySource
					? getRelatedItemDetailRoute(keySource, routePrimaryKeyFields.value, routeDetailRoute)
					: undefined,
			};
		});

		if (!hasMore.value) {
			return items;
		}

		return [
			...items,
			{ type: 'separator' },
			{ slot: 'view-more', to: viewMoreRoute.value },
		];
	});

	return {
		dropdownItems,
		displayTemplate,
	};
}
